import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { getErrorMessage } from '@/utils/errorMessage';
import { FormService } from '../form.service';

export const useCreateForm = (onSuccess?: (form: Forms.Form) => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: Forms.CreateForm) => FormService.create(body),
		onSuccess: (form) => {
			queryClient.invalidateQueries({ queryKey: ['forms'] });
			toast.success('Formulário criado com sucesso!');
			if (onSuccess) onSuccess(form);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar formulário'));
		},
	});
};

export const usePatchForm = (onSuccess?: (form: Forms.Form) => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({ id, body }: { id: string; body: Forms.UpdateForm }) =>
			FormService.patch(id, body),
		onSuccess: (form) => {
			queryClient.invalidateQueries({ queryKey: ['forms'] });
			toast.success('Formulário atualizado com sucesso!');
			if (onSuccess) onSuccess(form);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao atualizar formulário'));
		},
	});
};

export const useRemoveForm = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (id: string) => FormService.remove(id),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['forms'] });
			toast.success('Formulário removido com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover formulário'));
		},
	});
};

export const useCreateFormQuestion = (
	onSuccess?: (question: Forms.FormQuestion) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			formId,
			body,
		}: {
			formId: string;
			body: Forms.CreateFormQuestion;
		}) => FormService.addQuestion(formId, body),
		onSuccess: (question, variables) => {
			queryClient.invalidateQueries({
				queryKey: ['form', variables.formId],
			});
			toast.success('Pergunta criada com sucesso!');
			if (onSuccess) onSuccess(question);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao criar pergunta'));
		},
	});
};

export const usePatchFormQuestion = (
	onSuccess?: (question: Forms.FormQuestion) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			formId,
			questionId,
			body,
		}: {
			formId: string;
			questionId: string;
			body: Forms.UpdateFormQuestion;
		}) => FormService.patchQuestion(formId, questionId, body),
		onSuccess: (question, variables) => {
			queryClient.invalidateQueries({
				queryKey: ['form', variables.formId],
			});
			toast.success('Pergunta atualizada com sucesso!');
			if (onSuccess) onSuccess(question);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao atualizar pergunta'));
		},
	});
};

export const useRemoveFormQuestion = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			formId,
			questionId,
		}: {
			formId: string;
			questionId: string;
		}) => FormService.removeQuestion(formId, questionId),
		onSuccess: (_question, variables) => {
			queryClient.invalidateQueries({
				queryKey: ['form', variables.formId],
			});
			toast.success('Pergunta removida com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao remover pergunta'));
		},
	});
};

export const useAddFormAudience = (formId: string, onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (body: Forms.CreateFormAudience) =>
			FormService.addAudience(formId, body),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['form', formId] });
			queryClient.invalidateQueries({ queryKey: ['forms'] });
			toast.success('Empresa vinculada ao formulário!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro ao vincular empresa ao formulário',
				),
			);
		},
	});
};

export const useRemoveFormAudience = (
	formId: string,
	onSuccess?: () => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: (audienceId: string) =>
			FormService.removeAudience(formId, audienceId),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ['form', formId] });
			queryClient.invalidateQueries({ queryKey: ['forms'] });
			toast.success('Empresa desvinculada do formulário.');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro ao desvincular empresa do formulário',
				),
			);
		},
	});
};

export const useSubmitFormAnswer = (onSuccess?: () => void) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			formId,
			body,
		}: {
			formId: string;
			body: Forms.SubmitFormAnswer;
		}) => FormService.submitAnswer(formId, body),
		onSuccess: (_data, variables) => {
			queryClient.invalidateQueries({ queryKey: ['forms', 'available'] });
			queryClient.invalidateQueries({
				queryKey: ['forms', 'available', 'employee', 'me'],
			});
			queryClient.invalidateQueries({
				queryKey: ['form', variables.formId],
			});
			queryClient.invalidateQueries({
				queryKey: ['forms', 'answers', 'me', 'analytics'],
			});
			toast.success('Respostas enviadas com sucesso!');
			if (onSuccess) onSuccess();
		},
		onError: (error: unknown) => {
			toast.error(
				getErrorMessage(
					error,
					'Erro ao enviar respostas do formulário',
				),
			);
		},
	});
};

export const useReorderFormQuestions = (
	onSuccess?: (questions: Forms.FormQuestion[]) => void,
) => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			formId,
			body,
		}: {
			formId: string;
			body: Forms.ReorderFormQuestions;
		}) => FormService.reorderQuestions(formId, body),
		onSuccess: (questions, variables) => {
			queryClient.invalidateQueries({
				queryKey: ['form', variables.formId],
			});
			queryClient.invalidateQueries({ queryKey: ['match'] });
			toast.success('Ordem das perguntas atualizada!');
			if (onSuccess) onSuccess(questions);
		},
		onError: (error: unknown) => {
			toast.error(getErrorMessage(error, 'Erro ao reordenar perguntas'));
		},
	});
};
